import { useState, useRef, useCallback } from 'react'
import { FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { documentService } from '@/services'
import { useLegacyUpdatesStream } from '@/hooks/useLegacyUpdatesStream'
import type { LegacyUpdateEvent } from '@/hooks/useLegacyUpdatesStream'

interface TextInputViewProps {
  onComplete: () => void
}

type UploadState = 'idle' | 'uploading' | 'processing' | 'completed' | 'error'

const MIN_CHARS = 100

export function TextInputView({ onComplete }: TextInputViewProps) {
  const [state, setState] = useState<UploadState>('idle')
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const completionTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Listen for SSE events once we have a documentId and are in processing state
  useLegacyUpdatesStream({
    entityTypes: ['document'],
    entityIds: documentId ? [documentId] : undefined,
    enabled: state === 'processing' && !!documentId,
    onUpdate: useCallback(
      (event: LegacyUpdateEvent) => {
        if (event.entityId !== documentId) return

        if (event.status === 'completed') {
          setState('completed')
          completionTimer.current = setTimeout(onComplete, 1500)
        } else if (event.status === 'failed') {
          setState('error')
          setError('Processing failed. Please try again.')
        }
      },
      [documentId, onComplete]
    ),
  })

  const handleSubmit = async () => {
    if (!text.trim()) return

    setState('uploading')
    setError(null)

    try {
      const file = new File([text], 'startup-context.txt', { type: 'text/plain' })
      const res = await documentService.uploadDocument(file, 'text')

      if (res.documentId) {
        // Set documentId first, then transition to processing state
        // This ensures the documentId is available when useLegacyUpdatesStream enables
        setDocumentId(res.documentId)
        // Use setTimeout to ensure documentId state update completes first
        setTimeout(() => setState('processing'), 0)
      } else {
        setState('completed')
        completionTimer.current = setTimeout(onComplete, 1500)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed. Please try again.'
      setError(message)
      setState('error')
    }
  }

  const handleRetry = () => {
    setState('idle')
    setError(null)
    setDocumentId(null)
  }

  const charCount = text.length
  const isTooShort = charCount > 0 && charCount < MIN_CHARS

  return (
    <Card className="flex flex-col border-none bg-card/75 backdrop-blur-sm shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-white/5 bg-black/20">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center text-primary">
            <FileText className="h-4 w-4" />
          </div>
          <h2 className="text-lg font-semibold text-white">
            Tell Us About Your Startup
          </h2>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Describe your startup — what you're building, who it's for, and what
          makes it unique. We'll extract key context automatically.
        </p>
      </div>

      {/* Content */}
      <div className="p-6 flex-1">
        {state === 'idle' && (
          <div className="space-y-4">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Tell us about your startup..."
              rows={6}
              className="w-full rounded-xl bg-secondary border border-white/5 p-4 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
              autoFocus
            />

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {isTooShort
                  ? `${MIN_CHARS - charCount} more characters recommended`
                  : charCount > 0
                    ? 'Looking good!'
                    : `${MIN_CHARS} characters recommended`}
              </span>
              <span>{charCount} characters</span>
            </div>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <Button
              className="w-full h-12 text-base bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20"
              onClick={handleSubmit}
              disabled={!text.trim() || isTooShort}
            >
              Submit & Process
            </Button>
          </div>
        )}

        {state === 'uploading' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
            <p className="text-sm text-foreground font-medium">Uploading your text...</p>
          </div>
        )}

        {state === 'processing' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
            <p className="text-sm text-foreground font-medium">
              Analyzing...
            </p>
            <p className="text-xs text-muted-foreground text-center">
              Extracting entities, classifying content, and building your context graph.
              This may take a moment.
            </p>
          </div>
        )}

        {state === 'completed' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <CheckCircle className="h-10 w-10 text-green-400" />
            <p className="text-sm text-foreground font-medium">
              Text processed successfully!
            </p>
            <p className="text-xs text-muted-foreground">
              Proceeding to next step...
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <p className="text-sm text-destructive text-center">{error}</p>
            <Button
              variant="ghost"
              onClick={handleRetry}
              className="hover:bg-secondary hover:text-white"
            >
              Try again
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
